/**
 * Ligne de la liste : groupe de POI, ligne d'étape ou zone manquante.
 * EF-RBK-002, EF-RBK-009, EF-RBK-012, EF-RBK-013.
 *
 * Une ligne de POI ne porte que des données de parcours : chevron, icônes,
 * rang de passage, kilomètres, D+, heure estimée. **Aucun nom.** Un nom est
 * une chaîne de longueur libre : il fait varier la hauteur des lignes et
 * repousse les valeurs hors du champ visuel, alors que celles-ci tiennent sur
 * une largeur connue et s'alignent en colonnes. Le nom reste porté par le nom
 * accessible de l'icône et par le dépliant.
 */

import type { JSX } from 'preact';
import type { CategoryKey } from '../../core/categories';
import type { LocalDateTime } from '../../core/local-datetime';
import type { Poi, PoiId, RoadbookDocument } from '../../core/model';
import type { DisplayRow } from '../../roadbook/display';
import type { PoiLineRef } from '../../roadbook/order';
import {
  formatArrival,
  formatDateTime,
  formatDistance,
  formatDuration,
  formatElevation,
  NOT_AVAILABLE,
  NOT_AVAILABLE_LABEL,
} from '../format';
import { Glyph } from '../icons';
import { ACTIONS, MARKERS } from '../labels';
import { PoiDetail } from './PoiDetail';
import { PoiIconButton } from './PoiIconButton';

/**
 * Valeurs relatives à la position courante, en mode course. Les lignes de POI
 * cessent alors d'afficher les valeurs absolues depuis le départ pour ne
 * montrer que ce qui reste à faire — EF-IHM-003.
 */
export interface RaceRowView {
  /** Progression retenue pour la position courante, en mètres. */
  readonly progressionMeters: number;
  /** D+ cumulé à cette progression, ou `null` si l'altitude est indisponible. */
  readonly elevationGainMeters: number | null;
  /** Durée restante avant d'atteindre la progression donnée, en secondes. */
  readonly secondsTo: (progressionMeters: number) => number | null;
}

export interface RoadbookRowProps {
  readonly row: DisplayRow;
  readonly document: RoadbookDocument;
  readonly pois: ReadonlyMap<PoiId, Poi>;
  readonly hiddenCategories: ReadonlySet<CategoryKey>;
  readonly routeLengthMeters: number;
  readonly elevationAvailable: boolean;
  readonly totalGainMeters: number | null;
  readonly startLocal: LocalDateTime | null;
  readonly highlighted: boolean;
  /** Ligne portant la mise en évidence de EF-MOD-003, le cas échéant. */
  readonly emphasizedLine: PoiLineRef | null;
  /** Dépliant ouvert : le détail s'affiche sous la ligne. */
  readonly expanded: boolean;
  readonly onToggleDetail: () => void;
  readonly onSearchZone: ((from: number, to: number) => void) | null;
  readonly searchZoneDisabledReason: string | null;
  /** Non nul en mode course, dès qu'une position a été retenue. */
  readonly raceView: RaceRowView | null;
}

export function RoadbookRow(props: RoadbookRowProps): JSX.Element {
  switch (props.row.kind) {
    case 'stop':
      return <StopRow {...props} row={props.row} />;
    case 'missingRange':
      return <MissingRow {...props} row={props.row} />;
    case 'group':
      return <GroupRow {...props} row={props.row} />;
  }
}

/**
 * Gouttière réservée à gauche des lignes sans dépliant, afin que les icônes de
 * toutes les lignes restent alignées en colonne (EF-RBK-002).
 */
function Gutter(): JSX.Element {
  return <span class="row__gutter" aria-hidden="true" />;
}

function StopRow(props: RoadbookRowProps & { row: Extract<DisplayRow, { kind: 'stop' }> }): JSX.Element {
  const stop = props.row.stop;
  const glyph =
    stop.type === 'start' ? 'depart' : stop.type === 'pause' ? 'pause' : stop.type === 'sleep' ? 'sommeil' : 'arrivee';
  const title =
    stop.type === 'start'
      ? 'Départ'
      : stop.type === 'pause'
        ? 'Pause'
        : stop.type === 'sleep'
          ? `Sommeil — nuit ${stop.nightIndex ?? 1}`
          : 'Arrivée';

  const details: string[] = [];
  if (stop.type === 'start') {
    details.push(formatDateTime(stop.startLocal));
    details.push(`${formatDistance(props.routeLengthMeters)} à parcourir`);
    details.push(`D+ ${props.elevationAvailable ? formatElevation(props.totalGainMeters) : 'indisponible'}`);
  } else if (stop.type === 'finish') {
    details.push(formatDateTime(stop.startLocal));
    details.push(`D+ ${props.elevationAvailable ? formatElevation(props.totalGainMeters) : 'indisponible'}`);
  } else {
    details.push(formatDuration(stop.durationSeconds ?? 0));
    details.push(
      `${formatArrival(stop.startLocal, props.startLocal)} → ${formatArrival(stop.endLocal, props.startLocal)}`,
    );
    details.push(`km ${(stop.progressionMeters / 1000).toFixed(1).replace('.', ',')}`);
  }

  return (
    <div class={props.highlighted ? 'row row--stop row--highlighted' : 'row row--stop'}>
      <Gutter />
      <span class="row__icons">
        <Glyph name={glyph} label={title} />
      </span>
      <div>
        <div class="row__name">{title}</div>
        <div class="row__values muted">
          {details.map((detail, index) => (
            <span key={detail}>
              {index > 0 ? <span class="separator"> – </span> : null}
              {detail}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function MissingRow(
  props: RoadbookRowProps & { row: Extract<DisplayRow, { kind: 'missingRange' }> },
): JSX.Element {
  const { from, to } = {
    from: props.row.range.fromProgressionMeters,
    to: props.row.range.toProgressionMeters,
  };
  const disabled = props.onSearchZone === null || props.searchZoneDisabledReason !== null;

  return (
    <div class="row row--missing">
      <Gutter />
      <span class="row__icons">
        <Glyph name="zone" label={MARKERS.missingSearch} />
      </span>
      <div style="flex:1;">
        <div class="row__name">{MARKERS.missingSearch}</div>
        <div class="row__values">
          km {(from / 1000).toFixed(1).replace('.', ',')} à {(to / 1000).toFixed(1).replace('.', ',')}
        </div>
        {props.searchZoneDisabledReason !== null ? (
          <p class="muted" style="margin:4px 0 0;">
            {props.searchZoneDisabledReason}
          </p>
        ) : null}
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          props.onSearchZone?.(from, to);
        }}
      >
        {ACTIONS.searchZone}
      </button>
    </div>
  );
}

function GroupRow(props: RoadbookRowProps & { row: Extract<DisplayRow, { kind: 'group' }> }): JSX.Element {
  const members = props.row.members;
  const emphasized = props.emphasizedLine;

  // Par dérogation à EF-RBK-012, la ligne mise en évidence porte ses propres
  // valeurs et non celles de l'ancre du groupe.
  const inGroup =
    emphasized === null
      ? null
      : (members.find(
          (member) => member.poiId === emphasized.poiId && member.passageIndex === emphasized.passageIndex,
        ) ?? null);
  const reference = inGroup ?? props.row.anchor;

  const poi = props.pois.get(reference.poiId);
  const passage = poi?.passages.find((item) => item.index === reference.passageIndex);
  const computed = passage?.computed ?? null;

  const gainFromStart = computed?.elevationGainFromStartMeters ?? null;
  const gainToFinish = computed?.elevationGainToFinishMeters ?? null;
  const race = props.raceView;

  // Un groupe de plusieurs POI porte ses valeurs sous ses icônes : celles-ci
  // sont toutes affichées, sur autant de rangs que nécessaire, sans jamais
  // déborder latéralement (EF-IHM-002).
  const classes = ['row', 'row--group'];
  if (members.length > 1) classes.push('row--stacked');
  if (props.highlighted) classes.push('row--highlighted');
  if (props.expanded) classes.push('row--expanded');

  return (
    <div class={classes.join(' ')}>
      <div class="row__head">
        {/*
          Le chevron est à gauche : c'est le bord que le pouce atteint en
          tenant l'appareil, et il ouvre le détail sans quitter la liste.
        */}
        <button
          type="button"
          class="row__toggle"
          aria-expanded={props.expanded}
          onClick={props.onToggleDetail}
        >
          <Glyph name="suivant" label={props.expanded ? ACTIONS.hideDetail : ACTIONS.showDetail} size={24} />
        </button>

        <span class="row__icons">
          {members.map((member) => {
            const memberPoi = props.pois.get(member.poiId);
            if (memberPoi === undefined) return null;
            return (
              <PoiIconButton
                key={`${member.poiId}#${member.passageIndex}`}
                poi={memberPoi}
                hiddenCategories={props.hiddenCategories}
                emphasized={
                  emphasized !== null &&
                  emphasized.poiId === member.poiId &&
                  emphasized.passageIndex === member.passageIndex
                }
                onOpen={props.onToggleDetail}
              />
            );
          })}
        </span>
      </div>

      <div class="row__values">
        {props.highlighted ? <strong>Prochain POI</strong> : null}
        {race === null ? (
          <>
            <span>
              {formatDistance(reference.progressionMeters)} / {formatDistance(props.routeLengthMeters - reference.progressionMeters)}
            </span>
            <span class="separator">–</span>
            <span aria-label={gainFromStart === null ? NOT_AVAILABLE_LABEL : undefined}>
              {props.elevationAvailable
                ? `${formatElevation(gainFromStart)} / ${formatElevation(gainToFinish)}`
                : MARKERS.elevationUnavailableField}
            </span>
            <span class="separator">–</span>
            <span aria-label={computed === null ? NOT_AVAILABLE_LABEL : undefined}>
              {computed === null ? NOT_AVAILABLE : formatArrival(computed.estimatedArrivalLocal, props.startLocal)}
            </span>
          </>
        ) : (
          <RaceValues
            race={race}
            progressionMeters={reference.progressionMeters}
            gainFromStartMeters={gainFromStart}
            elevationAvailable={props.elevationAvailable}
          />
        )}
      </div>

      {/* Une fiche par POI du groupe, dans l'ordre des icônes. */}
      {props.expanded ? (
        <div class="row__detail">
          {members.map((member) => {
            const memberPoi = props.pois.get(member.poiId);
            if (memberPoi === undefined) return null;
            return (
              <PoiDetail
                key={`${member.poiId}#${member.passageIndex}`}
                poi={memberPoi}
                document={props.document}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Distance restante, D+ restant et durée restante avant d'atteindre la ligne,
 * toutes trois comptées depuis la position courante.
 */
function RaceValues(props: {
  readonly race: RaceRowView;
  readonly progressionMeters: number;
  readonly gainFromStartMeters: number | null;
  readonly elevationAvailable: boolean;
}): JSX.Element {
  const remainingMeters = props.progressionMeters - props.race.progressionMeters;
  if (remainingMeters < 0) return <span class="muted">dépassé</span>;

  const gainHere = props.race.elevationGainMeters;
  const remainingGain =
    props.gainFromStartMeters === null || gainHere === null
      ? null
      : Math.max(0, props.gainFromStartMeters - gainHere);
  const seconds = props.race.secondsTo(props.progressionMeters);

  return (
    <>
      <span>{formatDistance(remainingMeters)}</span>
      <span class="separator">–</span>
      <span aria-label={remainingGain === null ? NOT_AVAILABLE_LABEL : undefined}>
        {props.elevationAvailable ? formatElevation(remainingGain) : MARKERS.elevationUnavailableField}
      </span>
      <span class="separator">–</span>
      <span aria-label={seconds === null ? NOT_AVAILABLE_LABEL : undefined}>
        {seconds === null ? NOT_AVAILABLE : formatDuration(seconds)}
      </span>
    </>
  );
}
